import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const BASE_URL = 'https://bhumi.bd';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_land-guides-bd/artifacts/h77ykfwj_IMG_20260120_193556.png';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_land-guides-bd/artifacts/nbntooi2_IMG_20260121_003918-Picsart-AiImageEnhancer.png';

/**
 * SEO Component - Adds meta tags, Open Graph, Twitter Cards, and JSON-LD schema
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.path - URL path (e.g., '/guides/namjari')
 * @param {string} props.image - OG image URL
 * @param {string} props.type - 'website' | 'article' | 'profile'
 * @param {Object} props.article - Article metadata {publishedTime, modifiedTime, author, section, tags}
 * @param {Object} props.schema - Additional JSON-LD schema
 * @param {boolean} props.noIndex - Don't index this page
 */
const SEO = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  article = null,
  schema = null,
  noIndex = false,
}) => {
  const { language } = useLanguage();
  
  const fullUrl = `${BASE_URL}${path}`;
  
  // Handle title - don't add suffix if title already contains it
  let fullTitle;
  if (!title) {
    fullTitle = 'ভূমি - বাংলাদেশের জমি সংক্রান্ত তথ্য প্ল্যাটফর্ম';
  } else if (title.includes('ভূমি') || title.includes('Bhumi')) {
    fullTitle = title;
  } else {
    fullTitle = `${title} | ভূমি`;
  }
  
  const metaDescription = description || 'বাংলাদেশের জমি সংক্রান্ত তথ্য ও সেবা প্ল্যাটফর্ম। নামজারি, খতিয়ান, দলিল এবং যাচাইকৃত সেবাদাতা।';

  // Organization Schema (appears on all pages)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ভূমি',
    alternateName: 'Bhumi',
    url: BASE_URL,
    logo: LOGO_URL,
    description: 'বাংলাদেশের জমি সংক্রান্ত তথ্য ও সেবা প্ল্যাটফর্ম',
    areaServed: {
      '@type': 'Country',
      name: 'Bangladesh',
    },
    knowsAbout: [
      'নামজারি',
      'খতিয়ান',
      'দলিল',
      'ভূমি রেজিস্ট্রেশন',
      'Land Registration Bangladesh',
      'Namjari',
      'Khatian',
    ],
  };

  // WebSite Schema with SearchAction
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ভূমি',
    alternateName: 'Bhumi',
    url: BASE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/guides?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: ['bn', 'en'],
  };

  // Breadcrumb Schema
  const breadcrumbSchema = path ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'হোম',
        item: BASE_URL,
      },
      ...(path.split('/').filter(Boolean).map((segment, index, arr) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: segment.charAt(0).toUpperCase() + segment.slice(1),
        item: `${BASE_URL}/${arr.slice(0, index + 1).join('/')}`,
      }))),
    ],
  } : null;

  // Article Schema for blog/guide pages
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: metaDescription,
    image: image,
    author: {
      '@type': 'Organization',
      name: 'ভূমি',
    },
    publisher: {
      '@type': 'Organization',
      name: 'ভূমি',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    datePublished: article.publishedTime,
    dateModified: article.modifiedTime || article.publishedTime,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl,
    },
    articleSection: article.section,
    keywords: article.tags?.join(', '),
    inLanguage: language === 'en' ? 'en-BD' : 'bn-BD',
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language === 'en' ? 'en' : 'bn'} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Language Alternates */}
      <link rel="alternate" hrefLang="bn" href={`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}lang=bn`} />
      <link rel="alternate" hrefLang="en" href={`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="ভূমি" />
      <meta property="og:locale" content={language === 'en' ? 'en_BD' : 'bn_BD'} />
      <meta property="og:locale:alternate" content={language === 'en' ? 'bn_BD' : 'en_BD'} />
      
      {/* Article specific OG tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime || article.publishedTime} />
          <meta property="article:section" content={article.section} />
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO Meta */}
      <meta name="author" content="ভূমি" />
      <meta name="geo.region" content="BD" />
      <meta name="geo.placename" content="Bangladesh" />
      <meta name="content-language" content={language === 'en' ? 'en' : 'bn'} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Pre-built schema generators
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateServiceSchema = (service) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: service.name,
  description: service.bio,
  telephone: service.phone,
  email: service.email,
  address: {
    '@type': 'PostalAddress',
    addressLocality: service.location,
    addressCountry: 'BD',
  },
  priceRange: service.fee_range,
  aggregateRating: service.total_reviews > 0 ? {
    '@type': 'AggregateRating',
    ratingValue: service.avg_rating,
    reviewCount: service.total_reviews,
  } : undefined,
  areaServed: {
    '@type': 'Country',
    name: 'Bangladesh',
  },
});

export const generateHowToSchema = (guide) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: guide.title,
  description: guide.summary,
  totalTime: 'PT30M',
  tool: [],
  supply: [],
  step: guide.steps?.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.title || `Step ${index + 1}`,
    text: step.content,
  })) || [],
});
